import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError, errorText } from '../api/client'
import type { Enrollment, EnrollmentDetail, Playbook, PlaybookStep, Subject, WorkflowStep } from '../api/types'
import { shortDate } from '../lib/format'
import { subjectKey } from '../lib/subject'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { KIND, KindIcon, StepTrail } from './StepKind'
import { Loading } from './States'
import { useToast } from './Toast'

// Officers see these as "workflows". The code, the API and the database keep the
// older name "playbook" — renaming storage buys nothing and risks breaking rows.

/** Templates say {first_name}. When the dialog knows who it's for, show their
 *  actual name; otherwise a bracketed placeholder that reads as one, rather than
 *  the raw template or a pronoun that makes the sentence nonsense. */
export function readable(text: string | undefined, firstName?: string | null): string {
  return (text ?? '')
    .replace(/\{first_name\}/g, firstName || '[first name]')
    .replace(/\{lo_first\}|\{lo_name\}/g, '[your name]')
    .replace(/\{(\w+)\}/g, '')
}

/** "Day 0 · Call them — new lead", with waits folded into the day count, so an
 *  officer can read the whole plan as a schedule. */
function schedule(steps: PlaybookStep[]): { day: number; step: PlaybookStep }[] {
  let day = 0
  const out: { day: number; step: PlaybookStep }[] = []
  for (const step of steps) {
    if (step.type === 'wait') {
      day += step.days ?? 0
      continue
    }
    out.push({ day, step })
  }
  return out
}

function stepSummary(step: PlaybookStep, firstName?: string | null): string {
  if (step.type === 'task') return `Task for you: ${readable(step.title, firstName)}`
  if (step.type === 'sms') {
    const text = readable(step.body, firstName)
    return `Text: “${text.slice(0, 110)}${text.length > 110 ? '…' : ''}”`
  }
  if (step.type === 'email') return `Email: ${readable(step.subject, firstName)}`
  return `Wait ${step.days} days`
}

export function PlanPreview({ playbook, firstName }: { playbook: Playbook; firstName?: string | null }) {
  return (
    <ol className="plan">
      {schedule(playbook.steps).map(({ day, step }, i) => (
        <li key={i}>
          <span className="plan-day">{day === 0 ? 'Today' : `Day ${day}`}</span>
          <span className={`k-${step.type}`} style={{ color: 'var(--k)', display: 'inline-flex', transform: 'translateY(3px)' }}>
            <Icon name={KIND[step.type].icon} size={15} />
          </span>
          <span>{stepSummary(step, firstName)}</span>
        </li>
      ))}
    </ol>
  )
}

/** Pick a workflow and start it for this person or loan. Each one shows its whole
 *  schedule first, so nobody starts something without knowing what it will send. */
export function PlaybookModal({ open, onClose, subject, onEnrolled, firstName }: {
  open: boolean
  onClose: () => void
  subject: Subject
  onEnrolled?: () => void
  /** Who it's for, so the schedule preview reads "Call John", not "Call [first name]". */
  firstName?: string | null
}) {
  const { data, loading } = useAsync(
    () => (open ? api.playbooks() : Promise.resolve(null)),
    [open],
  )
  // A drip runs one of these same workflows for a list of people at a pace, so
  // "add them to one that is already running" is a way of starting a workflow,
  // not a separate idea needing a button of its own.
  const { data: drips } = useAsync(
    () => (open ? api.drips() : Promise.resolve(null)),
    [open],
  )
  const running = (drips?.drips ?? []).filter((d) => d.status !== 'stopped')
  const contactId = 'contact_id' in subject ? subject.contact_id : null
  const [expanded, setExpanded] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const toast = useToast()

  async function start(key: string, name: string) {
    try {
      await api.enroll(key, subject)
      toast(`Started ${name}`, { tone: 'positive' })
      onEnrolled?.()
      onClose()
    } catch (e) {
      const dup = e instanceof ApiError && e.isConflict
      toast(dup ? `${name} is already running for them` : errorText(e), { tone: dup ? 'warning' : 'danger' })
    }
  }

  async function join(id: number, name: string, live: boolean) {
    if (!contactId) return
    try {
      await api.addToDrip(id, [contactId])
      toast(live ? `Added to “${name}”` : `Will be included when “${name}” launches`,
            { tone: 'positive' })
      onEnrolled?.()
      onClose()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  // Your own workflows come first — you built them for exactly this moment, and
  // filed under a category they sit below four ready-made ones nobody edited.
  const all = (data?.playbooks ?? []).filter(pb => `${pb.name} ${pb.description ?? ''} ${pb.category}`.toLowerCase().includes(search.toLowerCase()))
  const groups = new Map<string, Playbook[]>()
  const mine = all.filter((pb) => pb.custom && pb.scope === 'mine')
  const branch = all.filter((pb) => pb.custom && pb.scope === 'branch')
  if (mine.length) groups.set('Yours', mine)
  if (branch.length) groups.set('Shared with the branch', branch)
  for (const pb of all.filter((x) => !x.custom)) {
    groups.set(pb.category, [...(groups.get(pb.category) ?? []), pb])
  }

  return (
    <Modal open={open} onClose={onClose} title="Start a workflow" width={700} className="workflow-picker-dialog"
      subtitle="Choose a sequence for this contact or loan. Review its schedule before starting.">
      {loading || !data ? (
        <Loading />
      ) : (
        <div className="workflow-picker-content">
          <div className="workflow-search"><Icon name="search" size={18}/><input aria-label="Find a workflow to start" placeholder="Search by name or purpose…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
          {data.messaging.mode === 'simulated' ? (
            <div className="notice warning">
              <Icon name="science" />
              <span>Messaging is in test mode: texts and emails are checked and recorded on the timeline, but not delivered. Tasks are created for real.</span>
            </div>
          ) : null}
          {/* Build it for the person you are standing on. Without this the builder
              opens with nobody chosen and the contact has to be found again. */}
          <Link to={contactId ? `/workflows?create=1&for=${contactId}` : '/workflows?create=1'}
            className="card wf-choice" style={{ color: 'inherit' }} onClick={onClose}>
            <Icon name="add" size={20} style={{ color: 'var(--accent)' }} />
            <span>
              <span style={{ fontWeight: 600, display: 'block' }}>Build your own</span>
              <span className="quiet" style={{ fontSize: 12.5 }}>Describe it and we’ll draft it, write your own steps, or copy one of these.</span>
            </span>
          </Link>
          {contactId && running.length ? (
            <div>
              <div className="palette-group" style={{ padding: '0 0 6px' }}>
                OR JOIN A CAMPAIGN ALREADY RUNNING
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {running.map((d) => (
                  <button key={d.id} className="card wf-choice"
                    onClick={() => join(d.id, d.name, d.status === 'active')}>
                    <Icon name="water_drop" size={20} style={{ color: 'var(--accent)' }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600, display: 'block' }}>{d.name}</span>
                      <span className="quiet" style={{ fontSize: 12.5 }}>
                        {d.workflow_name} · {d.status === 'active'
                          ? 'running — they join at its normal pace'
                          : `${d.status} — they are included when it launches`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {!all.length ? <p className="quiet">No workflows match your search.</p> : null}
          {[...groups.entries()].map(([category, items]) => (
            <div key={category}>
              <div className="palette-group" style={{ padding: '0 0 6px' }}>{category.toUpperCase()}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((pb) => {
                  const isOpen = expanded === pb.key
                  const touches = pb.steps.filter((s) => s.type !== 'wait').length
                  const days = pb.steps.reduce((a, s) => a + (s.type === 'wait' ? s.days ?? 0 : 0), 0)
                  return (
                    <div key={pb.key} className="workflow-picker-row">
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                            {pb.name}{pb.custom ? <span className="badge t-neutral" style={{ marginLeft: 8 }}>{pb.scope === 'branch' ? 'Branch' : 'Yours'}</span> : null}
                          </div>
                          <p style={{ margin: '3px 0 6px', fontSize: 12.5, color: 'var(--ink-2)' }}>{pb.description}</p>
                          <button className="linkbtn" onClick={() => setExpanded(isOpen ? null : pb.key)} aria-expanded={isOpen}>
                            {touches} step{touches === 1 ? '' : 's'} over {days === 0 ? 'one day' : `${days} days`} ·{' '}
                            {isOpen ? 'hide the schedule' : 'see the schedule'}
                          </button>
                        </div>
                        <button className="btn primary" onClick={() => start(pb.key, pb.name)}>Start</button>
                      </div>
                      {isOpen ? <PlanPreview playbook={pb} firstName={firstName} /> : null}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}


const OUTCOME: Record<string, { text: string; tone: string }> = {
  sent: { text: 'Sent', tone: 't-positive' },
  simulated: { text: 'Test mode — not delivered', tone: 't-warning' },
  blocked: { text: 'Not sent', tone: 't-danger' },
  failed: { text: 'Failed', tone: 't-danger' },
}

function when(step: WorkflowStep): string {
  if (!step.at) return ''
  const d = new Date(step.at)
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  if (step.state === 'done') return sameDay ? 'Today' : shortDate(step.at)
  if (d <= today) return 'Due now'
  return sameDay ? 'Later today' : shortDate(step.at)
}

/** A running workflow in full: what it already did, what's next and when, and
 *  what comes after — with pause, stop and "do the next step now". */
export function WorkflowModal({ enrollmentId, onClose, onChange }: {
  enrollmentId: number
  onClose: () => void
  onChange?: () => void
}) {
  const [bump, setBump] = useState(0)
  const { data, loading } = useAsync(() => api.enrollment(enrollmentId), [enrollmentId, bump])
  const [busy, setBusy] = useState(false)
  const [openBody, setOpenBody] = useState<number | null>(null)
  const toast = useToast()

  async function act(fn: () => Promise<unknown>, message: string) {
    setBusy(true)
    try {
      await fn()
      toast(message, { tone: 'positive' })
      setBump((n) => n + 1)
      onChange?.()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const d: EnrollmentDetail | null = data
  const live = d?.status === 'active' || d?.status === 'paused'
  const visible = (d?.steps ?? []).filter((s) => s.type !== 'wait')
  const doneCount = visible.filter((s) => s.state === 'done').length

  return (
    <Modal open onClose={onClose} width={660} className="workflow-detail-dialog" title={d?.name ?? 'Workflow'}
      subtitle={d?.description ?? undefined}
      footer={d && live ? (
        <>
          <button className="btn" disabled={busy}
            onClick={() => act(() => api.setEnrollment(d.id, 'stopped'), 'Workflow stopped')}>
            <Icon name="stop" size={16} /> Stop
          </button>
          <button className="btn" disabled={busy}
            onClick={() => act(() => api.setEnrollment(d.id, d.status === 'active' ? 'paused' : 'active'),
              d.status === 'active' ? 'Paused' : 'Resumed')}>
            <Icon name={d.status === 'active' ? 'pause' : 'play_arrow'} size={16} />
            {d.status === 'active' ? 'Pause' : 'Resume'}
          </button>
          <span className="spacer" />
          <button className="btn primary" disabled={busy || d.status !== 'active'}
            title={d.status !== 'active' ? 'Resume it first' : 'Skip the wait and do the next step now'}
            onClick={() => act(() => api.runEnrollmentNow(d.id), 'Done — next step ran')}>
            <Icon name="fast_forward" size={16} /> Do next step now
          </button>
        </>
      ) : <button className="btn" onClick={onClose}>Close</button>}>
      {loading && !d ? <Loading /> : !d ? null : (
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <span className={`badge ${d.status === 'active' ? 't-positive' : d.status === 'paused' ? 't-warning' : 't-neutral'}`}>
              {d.status === 'active' ? 'Running' : d.status === 'paused' ? 'Paused' : d.status === 'done' ? 'Finished' : 'Stopped'}
            </span>
            <span className="quiet" style={{ fontSize: 12.5 }}>
              {doneCount} of {visible.length} steps done · started {shortDate(d.created_at)}
            </span>
          </div>
          <ol className="wf">
            {d.steps.map((s) => {
              if (s.type === 'wait') {
                return s.state === 'done' ? null : (
                  <li key={s.index} className="wf-wait">then wait {s.days} day{s.days === 1 ? '' : 's'}</li>
                )
              }
              const outcome = s.outcome ? OUTCOME[s.outcome] : null
              const hasBody = Boolean(s.body) && s.type !== 'task'
              return (
                <li key={s.index} className={`wf-step wf-${s.state}`}>
                  <KindIcon kind={s.type} state={s.state} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={`wf-kind k-${s.type}`}>{KIND[s.type].short}{s.state === 'done' ? ' · done' : s.state === 'next' ? ' · next' : ''}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                      <span className="wf-label">
                        {s.type === 'task' ? s.title : s.type === 'email' ? `Email: ${s.subject}` : 'Text message'}
                      </span>
                      <span className="spacer" />
                      <span className="mono" style={{ fontSize: 11.5, color: s.state === 'next' ? 'var(--accent)' : 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                        {when(s)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {s.type === 'task' && s.state === 'done' ? (
                        <span className={`badge ${s.task_done ? 't-positive' : 't-neutral'}`}>
                          {s.task_done ? 'Task completed' : 'On your task list'}
                        </span>
                      ) : null}
                      {outcome ? <span className={`badge ${outcome.tone}`}>{outcome.text}</span> : null}
                      {hasBody ? (
                        <button className="linkbtn" onClick={() => setOpenBody(openBody === s.index ? null : s.index)}>
                          {openBody === s.index ? 'hide message' : 'show message'}
                        </button>
                      ) : null}
                    </div>
                    {s.reason && s.outcome !== 'sent' && s.outcome !== 'simulated' ? (
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 4 }}>{s.reason} — a task was added for you instead.</div>
                    ) : null}
                    {openBody === s.index ? <div className="tl-body wf-body">{s.body}</div> : null}
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      )}
    </Modal>
  )
}

/** Workflows on a person or loan. Each row opens the full schedule. */
export function EnrollmentList({ subject, refreshKey = 0, onChange, onAdd }: {
  subject: Subject
  refreshKey?: number
  onChange?: () => void
  /** Opens the picker, so an empty panel is a way in rather than a dead end. */
  onAdd?: () => void
}) {
  const [bump, setBump] = useState(0)
  const { data } = useAsync(() => api.enrollments(subject), [subjectKey(subject), refreshKey, bump])
  const [open, setOpen] = useState<number | null>(null)
  const items = data?.enrollments ?? []

  const changed = () => {
    setBump((n) => n + 1)
    onChange?.()
  }

  if (!items.length) {
    return (
      <p className="quiet" style={{ margin: 0 }}>
        No workflows running.{onAdd ? <> <button className="linkbtn" onClick={onAdd}>Start one</button> — yours and the ready-made ones.</> : null}
      </p>
    )
  }
  return (
    <div>
      {items.map((e: Enrollment) => {
        const live = e.status === 'active' || e.status === 'paused'
        return (
          <button key={e.id} className="taskrow wf-row" onClick={() => setOpen(e.id)}>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <span className="task-title" style={{ display: 'block' }}>{e.name}</span>
              <span style={{ display: 'block', margin: '6px 0 4px' }}>
                <StepTrail kinds={e.kinds} done={e.done} status={e.status} />
              </span>
              <span className="task-meta">
                {live
                  ? <span>{e.status === 'paused' ? 'Paused' : `Next ${shortDate(e.next_run_at)}`} · {readableNext(e.next_step)}</span>
                  : <span>{e.status === 'done' ? 'Finished' : 'Stopped'}</span>}
              </span>
            </span>
            <Icon name="chevron_right" size={18} style={{ color: 'var(--ink-4)' }} />
          </button>
        )
      })}
      {open !== null ? <WorkflowModal enrollmentId={open} onClose={() => setOpen(null)} onChange={changed} /> : null}
    </div>
  )
}

export function readableNext(label: string | null, firstName?: string | null): string {
  if (!label) return 'finishing'
  return readable(label.replace(/^Task: /, '').replace(/^Email: /, 'email: ').replace(/^Text message$/, 'a text'), firstName)
}

